<script>
import MottoIcon from '@components/motto-icon.vue'
import FavoriteHeart from '@components/favorite-heart.vue'
import LoaderIcon from '@components/loader-icon.vue'
import CopyToClipboardButton from '@components/copy-to-clipboard-button.vue'
import ResourceDownloadFile from '@components/resources/resource-download-file.vue'
import { getResourceById, addResourceViewCount, addFavoriteResource, deleteFavoriteResource } from '@src/api/resources'
import { transform as transformResource } from '@src/api/transformers/resourceTransformer'
import { downloadResourceAssetMixin } from '@src/mixins/downloadResourceAssetMixin'
import _ from 'lodash'
import { resourcesComputed } from '@state/helpers'

export default {
  components: {
    MottoIcon,
    FavoriteHeart,
    LoaderIcon,
    CopyToClipboardButton,
    ResourceDownloadFile,
  },
  mixins: [downloadResourceAssetMixin],
  props: {
    toastComponent: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      loading: true,
      loadingFavorite: false,
      error: false,
      resource: {},
      resourceId: '',
      modalIdentifier: 'view-resource-modal',
      bvToast: this.toastComponent || this.$bvToast,
      department: 'Resources',
      youtubeVideoId: null,
      vimeoVideoId: null,
    }
  },
  computed: {
    ...resourcesComputed,
    isFavorite() {
      return this.isResourceFavorite(this.resourceId)
    },

    resourceUrl() {
      return window.location.origin + '/resource/' + this.resource.resourceId
    },
  },
  created() {
    this.resourceId = this.$store.getters['resources/selectedResourceId']
    this.fetchResource(this.resourceId)
  },
  methods: {
    async fetchResource(resourceId) {
      try {
        const { data: resource } = await getResourceById({ resourceId })
        this.resource = transformResource(resource)
        this.verifyVideo()
        await addResourceViewCount({ resourceId })
      } catch (err) {
        this.bvToast.toast('There was an error loading this resource. Please try again later', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
        this.closeModal()
      } finally {
        this.loading = false
      }
    },

    verifyVideo() {
      if (this.resource.assetType !== 'video' || !this.resource.assetUrl) {
        return
      }
      const youtubeVideoIdFromURL = this.$youtube.getIdFromURL(this.resource.assetUrl)
      if (youtubeVideoIdFromURL !== this.resource.assetUrl) {
        this.youtubeVideoId = youtubeVideoIdFromURL
      }
      if (this.resource.assetUrl.indexOf('vimeo') !== -1) {
        this.vimeoVideoId = this.resource.assetUrl
      }
    },

    closeModal() {
      this.$bvModal.hide(this.modalIdentifier)
    },

    clickBreadcrumb(type) {
      this.closeModal()
      let category = null
      let subcategory = null
      if (type === 'category') {
        category = this.resource.categoryName.toLowerCase()
      }
      if (type === 'subcategory') {
        category = this.resource.categoryName.toLowerCase()
        subcategory = this.resource.subcategoryName.toLowerCase()
      }
      const loadedCategory = !_.isEmpty(this.$route.params.category) ? this.$route.params.category.toLowerCase() : null
      const loadedSubcategory = !_.isEmpty(this.$route.params.subcategory)
        ? this.$route.params.subcategory.toLowerCase()
        : null
      const resourceId = !_.isEmpty(this.$route.params.resourceId) ? this.$route.params.resourceId : null
      if (category !== loadedCategory || subcategory !== loadedSubcategory || resourceId) {
        this.$router.push({
          name: 'resources',
          params: {
            category: category,
            subcategory: subcategory,
          },
        })
      }
    },

    async favoriteResource() {
      await addFavoriteResource({ resourceId: this.resourceId })
      this.$store.commit('resources/FAVORITE_RESOURCE_BY_ID', this.resourceId)
    },

    async unfavoriteResource() {
      await deleteFavoriteResource({ resourceId: this.resourceId })
      this.$store.commit('resources/UNFAVORITE_RESOURCE_BY_ID', this.resourceId)
    },

    async handleHeartClick() {
      if (this.loadingFavorite) {
        return
      }
      this.loadingFavorite = true
      if (this.isFavorite) {
        await this.unfavoriteResource()
      } else {
        await this.favoriteResource()
      }
      this.loadingFavorite = false
    },
  },
}
</script>

<template>
  <div id="modal-resource-container" :class="$style.container" data-modal-resource>
    <LoaderIcon v-if="loading || error" :error="error" class="m-auto"></LoaderIcon>
    <Transition name="motto-resource-slide">
      <div v-if="!loading && !error" :class="$style.modalResourceContainer">
        <MottoIcon
          :class="$style.closeButton"
          name="X"
          icon-color="gray"
          data-close-modal-resource-button
          @click.native="closeModal" />
        <div :class="$style.contentContainer">
          <div :class="$style.contentHeader">
            <div :class="$style.breadcrumbs">
              <p class="p2 color-darkest-gray cursor-pointer" @click="clickBreadcrumb">RESOURCES</p>
              <BaseIcon v-if="resource.categoryName" class="color-gray" name="chevron-right" />
              <p
                v-if="resource.categoryName"
                class="p2 color-darkest-gray cursor-pointer"
                @click="clickBreadcrumb('category')">
                {{ resource.categoryName.toUpperCase() }}
              </p>
              <BaseIcon v-if="resource.subcategoryName" class="color-gray" name="chevron-right" />
              <p
                v-if="resource.subcategoryName"
                class="p2 color-darkest-gray cursor-pointer"
                @click="clickBreadcrumb('subcategory')">
                {{ resource.subcategoryName.toUpperCase() }}
              </p>
            </div>
          </div>
          <div class="position-relative">
            <BaseLink
              :class="$style.resourceTitleLink"
              :to="{
                name: 'resource',
                params: {
                  resourceId,
                },
              }">
              <h2 :class="$style.resourceTitle" class="color-primary-blue">{{ resource.title }}</h2>
            </BaseLink>
            <LoaderIcon v-if="loadingFavorite" class="d-inline" :class="$style.heartIcon" />
            <FavoriteHeart
              v-else
              :class="$style.heartIcon"
              :is-favorite="isFavorite"
              :options="{ width: 30, height: 30 }"
              @handleHeartClick="handleHeartClick" />
            <CopyToClipboardButton
              v-if="resource.resourceId"
              :text-to-copy="resourceUrl"
              :class="$style.clipboardButton" />
          </div>
          <p class="p1 color-dark-gray">{{ resource.revisedAt }}</p>
          <p :class="$style.resourceTextContent" class="p1 color-primary-charcoal" v-html="resource.content"></p>
          <div v-if="resource.tags.length" :class="$style.resourceTags" class="tagsContainer">
            <span v-for="tag in resource.tags" :key="tag" class="tag-text">{{ tag }}</span>
          </div>

          <youtube
            v-if="youtubeVideoId"
            :class="$style.frameVideo"
            :video-id="youtubeVideoId"
            player-width="411"
            player-height="231"></youtube>

          <vimeo-player
            v-if="vimeoVideoId"
            :class="$style.frameVideo"
            :video-id="vimeoVideoId"
            player-width="411"
            player-height="231" />

          <ResourceDownloadFile v-if="resource.isDownloadable" :resource="resource" :modal-button="true" />
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.container {
  @media (max-width: $lg-breakpoint) {
    width: 100vw;
    min-width: 100vw;
  }

  display: flex;
  min-width: 601px;
  min-height: 100%;
}

.modalResourceContainer {
  @media (max-width: $lg-breakpoint) {
    padding: 90px 30px 0 30px;
  }

  position: relative;
  display: flex;
  padding: 40px 80px 0 110px;
}

.breadcrumbs {
  display: flex;

  > svg {
    margin: 0 13px;
  }
}

.closeButton {
  position: absolute;
  top: 40px;
  left: 40px;
  cursor: pointer;
}

.contentContainer {
  @media (max-width: $lg-breakpoint) {
    width: 90%;
  }

  display: flex;
  flex-direction: column;
  width: min-content;
  min-width: 411px;
  padding-bottom: 72px;
}

.contentHeader {
  display: flex;
  align-items: flex-end;
}

.heartIcon {
  @media (max-width: $lg-breakpoint) {
    top: 30px;
  }

  position: absolute;
  top: 72px;
  right: 54px;
}

.resourceTitleLink {
  text-decoration: none;
}

.resourceTitle {
  @media (max-width: $lg-breakpoint) {
    margin-top: 30px;
  }

  margin-top: 72px;
  margin-right: 92px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.resourceTextContent {
  margin-top: 32px !important;
  overflow-x: scroll;
  word-break: break-word;
}

.resourceTags {
  margin-top: 24px;
}

.previewFile {
  @media (max-width: $lg-breakpoint) {
    width: 327px;
    height: 186px;
    margin-top: 32px;
  }

  width: 411px;
  height: 234px;
  margin-top: 40px;
  overflow: hidden;
  cursor: pointer;
  background: $light-gray;
  border: 0.5px solid #d4d6d9;
  border-radius: 12px;

  &:hover {
    opacity: 0.8;
  }
}

.previewFileBox {
  @media (max-width: $lg-breakpoint) {
    width: 316px;
    height: 136px;
  }

  width: 400px;
  height: 171px;
  margin: 5px 5px 0 5px;
  overflow: hidden;
  background: $white;
  border-radius: 8px;
}

.previewFileIcon {
  padding-top: 24px;

  svg {
    @media (max-width: $lg-breakpoint) {
      width: 316px;
      height: 86px;
    }

    width: 400px;
    height: 120px;
  }

  span {
    @media (max-width: $lg-breakpoint) {
      font-size: 20px;
    }

    font-size: 28px;
  }
}

.previewFileName {
  @media (max-width: $lg-breakpoint) {
    width: 202px;
    height: 44px;
    margin-left: 24px;
  }

  position: absolute;
  width: 270px;
  height: 56px;
  margin-left: 45px;
  overflow: hidden;

  p {
    @media (max-width: $lg-breakpoint) {
      line-height: 14px;
    }

    position: absolute;
    top: 50%;
    width: 100%;
    font-size: 12px;
    font-weight: 700;
    color: $dark-gray;
    transform: translate(0, -50%);
  }
}

.previewFileSize {
  @media (max-width: $lg-breakpoint) {
    width: 52px;
    height: 44px;
    margin-left: 249px;
  }

  position: absolute;
  width: 52px;
  height: 56px;
  margin-left: 325px;
  text-align: right;

  p {
    position: absolute;
    top: 50%;
    width: 100%;
    font-size: 10px;
    color: $dark-gray;
    transform: translate(0, -50%);
  }
}

.frameVideo {
  iframe {
    @media (max-width: $lg-breakpoint) {
      width: 319px;
      height: 180px;
      border-radius: 11px;
    }
  }

  margin-top: 48px;
  margin-bottom: 60px;
}

.clipboardButton {
  @media (max-width: $lg-breakpoint) {
    top: 23px;
  }

  position: absolute;
  top: 65px;
  right: 0;

  span {
    @media (max-width: $lg-breakpoint) {
      bottom: 43px;
      width: 83%;
      height: 48px;
      padding-top: 10px;
    }

    position: fixed;
    bottom: 112px;
    left: 110px;
    z-index: 1;
    width: 411px;
    height: 56px;
    padding-top: 14px;
    text-align: center;
    background: $primary-blue;
    border-radius: 8px;
    box-shadow: 1px 2px 10px rgba(0, 0, 0, 0.3);
    transition: opacity 0.5s ease;

    h4 {
      @media (max-width: $lg-breakpoint) {
        font-size: 12px;
      }

      div {
        @media (max-width: $lg-breakpoint) {
          margin-right: 10px;
        }

        margin-right: 16px;

        svg {
          @media (max-width: $lg-breakpoint) {
            width: 24px;
          }
        }
      }
    }
  }
}
</style>
